import { useState, useRef, useEffect } from "react";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { wouldMark } from "@/lib/trialMark";

const THEMES = [
  { name: "Indigo", bg: "#4F46E5", fg: "#ffffff", accent: "#C7D2FE" },
  { name: "Charcoal", bg: "#1F2937", fg: "#ffffff", accent: "#9CA3AF" },
  { name: "Emerald", bg: "#059669", fg: "#ffffff", accent: "#A7F3D0" },
  { name: "Rose", bg: "#E11D48", fg: "#ffffff", accent: "#FECDD3" },
  { name: "Cream", bg: "#FEF3C7", fg: "#1F2937", accent: "#78716C" },
  { name: "White", bg: "#ffffff", fg: "#111827", accent: "#6B7280" },
];

// Standard US business card: 3.5in x 2in at 300 DPI
const CARD_W = 1050;
const CARD_H = 600;

const TOOL_INFO_CONTENT = {
  what: `Designs a printable business card with your name, title, contact details, and one layout in a choice of six color themes, or your own uploaded photo as the background.`,
  how: `Enter your details, pick a design style or upload your own background photo, and download the finished card as a print-ready file. The PDF has 0.125 in of bleed on every edge, with the background carried through it.`,
  tip: `Check your local print shop's exact bleed and margin requirements before ordering physical cards — a design that looks fine on screen can end up with text too close to the trim edge once actually printed. If using a photo background, a darker overlay is applied automatically to keep text readable — a photo with less busy detail in the lower-left area (where your name and title sit) will look cleanest.`,
};

const FONT = '"Noto Sans", "Segoe UI", Arial, sans-serif';
const BLEED = 38; // 0.125 in at 300 DPI

/** Shrinks the font until the text fits, then trims with an ellipsis as a last resort. */
export function fitText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, size: number, minSize: number, weight = ""): string {
  let s = size;
  ctx.font = `${weight} ${s}px ${FONT}`.trim();
  while (ctx.measureText(text).width > maxWidth && s > minSize) {
    s -= 2;
    ctx.font = `${weight} ${s}px ${FONT}`.trim();
  }
  let t = text;
  while (ctx.measureText(t).width > maxWidth && t.length > 1) t = t.slice(0, -1);
  return t === text ? t : t.trimEnd() + "…";
}

interface CardOpts {
  side: "front" | "back";
  bleed: number; // extra pixels on every edge (0 for on-screen preview)
  name: string; title: string; company: string; tagline: string; phone: string; email: string; website: string;
  bg: string; fg: string; accent: string; bgImage: HTMLImageElement | null; fgOverride: string | null;
  stripe: boolean;
}

function renderCard(canvas: HTMLCanvasElement, o: CardOpts) {
  const W = CARD_W + o.bleed * 2;
  const H = CARD_H + o.bleed * 2;
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // The solid background is painted over the whole canvas first, so the bleed edge is never left unpainted.
  ctx.fillStyle = o.bg;
  ctx.fillRect(0, 0, W, H);
  // Background and accent run to the very edge (into the bleed).
  if (o.bgImage) {
    const imgRatio = o.bgImage.width / o.bgImage.height;
    let drawW: number, drawH: number, offsetX: number, offsetY: number;
    if (imgRatio > W / H) { drawH = H; drawW = H * imgRatio; offsetX = (W - drawW) / 2; offsetY = 0; }
    else { drawW = W; drawH = W / imgRatio; offsetX = 0; offsetY = (H - drawH) / 2; }
    ctx.drawImage(o.bgImage, offsetX, offsetY, drawW, drawH);
    ctx.fillStyle = "rgba(0, 0, 0, 0.45)"; // keeps white text legible on any photo
    ctx.fillRect(0, 0, W, H);
  } else {
    ctx.fillStyle = o.bg;
    ctx.fillRect(0, 0, W, H);
  }
  const fg = o.fgOverride ?? (o.bgImage ? "#ffffff" : o.fg);
  const accent = o.bgImage ? "#E5E7EB" : o.accent;

  if (o.stripe) {
    // A footer stripe: 24px (0.08 in) inside the trim line, carried through the bleed so no gap shows after trimming.
    ctx.fillStyle = o.bgImage ? "rgba(255, 255, 255, 0.85)" : o.accent;
    ctx.fillRect(0, H - o.bleed - 24, W, 24 + o.bleed);
  }

  // All text lives inside the trim area, 60px (0.2in) from the cut line.
  ctx.save();
  ctx.translate(o.bleed, o.bleed);
  const maxW = CARD_W - 120;
  ctx.textBaseline = "alphabetic";
  if (o.side === "back") {
    ctx.textAlign = "center";
    ctx.fillStyle = fg;
    ctx.fillText(fitText(ctx, o.company || o.name, maxW, 72, 28, "bold"), CARD_W / 2, CARD_H / 2);
    if (o.tagline) {
      ctx.fillStyle = accent;
      ctx.fillText(fitText(ctx, o.tagline, maxW, 32, 16), CARD_W / 2, CARD_H / 2 + 56);
    }
    ctx.restore();
    return;
  }
  ctx.textAlign = "left";
  ctx.fillStyle = fg;
  ctx.fillText(fitText(ctx, o.name, maxW, 54, 24, "bold"), 60, 140);
  ctx.fillStyle = accent;
  ctx.fillText(fitText(ctx, o.title, maxW, 28, 16), 60, 185);
  ctx.fillStyle = fg;
  ctx.fillText(fitText(ctx, o.company, maxW, 32, 18, "bold"), 60, 260);
  let y = 420;
  [o.phone, o.email, o.website].filter(Boolean).forEach(line => {
    ctx.fillStyle = fg;
    ctx.fillText(fitText(ctx, line, maxW, 24, 14), 60, y);
    y += 36;
  });
  ctx.restore();
}

export default function BusinessCard() {
  const [name, setName] = useState("Jane Doe");
  const [title, setTitle] = useState("Founder & CEO");
  const [company, setCompany] = useState("Acme Inc.");
  const [phone, setPhone] = useState("(555) 123-4567");
  const [email, setEmail] = useState("jane@acme.com");
  const [website, setWebsite] = useState("acme.com");
  const [tagline, setTagline] = useState("");
  const [side, setSide] = useState<"front" | "back">("front");
  const [themeIdx, setThemeIdx] = useState(0);
  const [customFg, setCustomFg] = useState<string | null>(null);
  const [bgImage, setBgImage] = useState<HTMLImageElement | null>(null);
  const [stripe, setStripe] = useState(true);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const theme = THEMES[themeIdx];
  const trialBadge = wouldMark("image/png", "business-card.png");

  const handlePhotoUpload = (file: File) => {
    const img = new Image();
    img.onload = () => setBgImage(img);
    img.onerror = () => setBgImage(null);
    img.src = URL.createObjectURL(file);
  };

  const optsFor = (sd: "front" | "back", bleed: number): CardOpts => ({
    side: sd, bleed, name, title, company, tagline, phone, email, website,
    bg: theme.bg, fg: theme.fg, accent: theme.accent, bgImage, fgOverride: customFg, stripe,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) renderCard(canvas, optsFor(side, 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, title, company, tagline, phone, email, website, theme, bgImage, customFg, side, stripe]);

  const { logUsageNow } = useLogUsageOnAction();

  // Was useLogToolUsage(true, 3000, { ownActions: true }) -- a debounced logger with no
  // gating, while handleDownload (exports the real card PNG) had zero
  // usage-limit check of its own.
  const handleDownload = async () => {
    const allowed = await logUsageNow();
    if (!allowed) return;
    const canvas = document.createElement("canvas");
    renderCard(canvas, optsFor(side, 0));
    canvas.toBlob(blob => {
      if (blob) downloadBlob(blob, `business-card-${side}-${(company || "card").toLowerCase().replace(/\s+/g, "-")}.png`);
    }, "image/png");
  };

  // Print-ready PDF: 3.75 x 2.25 in pages (3.5 x 2 in card + 0.125 in bleed on each edge),
  // front and back. Ask the printer for "bleed" artwork and trim at the card edge.
  const handlePdf = async () => {
    const allowed = await logUsageNow();
    if (!allowed) return;
    const { PDFDocument } = await import("pdf-lib");
    const pdf = await PDFDocument.create();
    for (const sd of ["front", "back"] as const) {
      const c = document.createElement("canvas");
      renderCard(c, optsFor(sd, BLEED));
      const blob: Blob | null = await new Promise(res => c.toBlob(res, "image/png"));
      if (!blob) continue;
      const img = await pdf.embedPng(new Uint8Array(await blob.arrayBuffer()));
      const page = pdf.addPage([(CARD_W + BLEED * 2) * 72 / 300, (CARD_H + BLEED * 2) * 72 / 300]);
      page.drawImage(img, { x: 0, y: 0, width: page.getWidth(), height: page.getHeight() });
    }
    const bytes = await pdf.save();
    downloadBlob(new Blob([new Uint8Array(bytes)], { type: "application/pdf" }), `business-card-print-${(company || "card").toLowerCase().replace(/\s+/g, "-")}.pdf`);
  };


  return (
    <ToolContainer>
      <div className="max-w-xl mx-auto space-y-6">
        <div className="flex justify-center gap-2 text-sm">
          {(["front", "back"] as const).map(k => (
            <button key={k} type="button" onClick={() => setSide(k)} className={`px-3 py-1 rounded-md border ${side === k ? "bg-primary text-primary-foreground" : "bg-card"}`}>{k === "front" ? "Front" : "Back"}</button>
          ))}
        </div>
        <div className="flex justify-center">
          <canvas ref={canvasRef} className="border rounded-lg shadow-sm w-full max-w-md h-auto" style={{ aspectRatio: `${CARD_W}/${CARD_H}` }} />
        </div>

        <div className="flex flex-wrap gap-2 justify-center items-center">
          {THEMES.map((t, i) => (
            <button
              key={t.name}
              onClick={() => { setThemeIdx(i); setBgImage(null); }}
              className={`w-8 h-8 rounded-full border-2 transition-transform ${!bgImage && i === themeIdx ? "scale-110 border-primary" : "border-border"}`}
              style={{ backgroundColor: t.bg }}
              title={t.name}
            />
          ))}
          <span className="text-xs text-muted-foreground mx-1">or</span>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handlePhotoUpload(f); }} />
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>Upload Photo Background</Button>
          {bgImage && <Button variant="ghost" size="sm" onClick={() => setBgImage(null)}>Remove Photo</Button>}
        </div>

        <div className="flex items-center justify-center gap-3">
          <Label className="text-xs text-muted-foreground shrink-0">Text Color</Label>
          <Input aria-label="Text Color"
            type="color"
            value={customFg ?? (bgImage ? "#ffffff" : theme.fg)}
            onChange={e => setCustomFg(e.target.value)}
            className="w-14 h-8 p-1"
          />
          {customFg && (
            <Button variant="ghost" size="sm" onClick={() => setCustomFg(null)}>Reset to theme default</Button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>Name</Label><Input aria-label="Name" className="text-base sm:text-sm" value={name} onChange={e => setName(e.target.value)} /></div>
          <div><Label>Title</Label><Input aria-label="Title" className="text-base sm:text-sm" value={title} onChange={e => setTitle(e.target.value)} /></div>
          <div><Label>Company</Label><Input aria-label="Company" className="text-base sm:text-sm" value={company} onChange={e => setCompany(e.target.value)} /></div>
          <div><Label>Phone</Label><Input aria-label="Phone" className="text-base sm:text-sm" value={phone} onChange={e => setPhone(e.target.value)} /></div>
          <div><Label>Email</Label><Input aria-label="Email" className="text-base sm:text-sm" value={email} onChange={e => setEmail(e.target.value)} /></div>
          <div><Label>Website</Label><Input aria-label="Website" className="text-base sm:text-sm" value={website} onChange={e => setWebsite(e.target.value)} /></div>
          <div className="sm:col-span-2"><Label>Back-of-card tagline (optional)</Label><Input aria-label="Back-of-card tagline (optional)" className="text-base sm:text-sm" value={tagline} onChange={e => setTagline(e.target.value)} placeholder="e.g. Quality you can count on" /></div>
        </div>

        <label className="flex items-center gap-2 text-sm justify-center min-h-[44px] sm:min-h-0"><input type="checkbox" className="h-5 w-5" checked={stripe} onChange={e => setStripe(e.target.checked)} />Footer accent stripe (turn off for a plain solid background)</label>

        {trialBadge && (
          <p className="text-xs text-muted-foreground text-center" data-testid="bc-trial-note">
            Trial exports carry a small "Created with FreeDailyPro.com" badge inside the card area (bottom right on the PDF; top and bottom right on the PNG). A day or monthly pass exports without it. Keep those corners free of text if you want to print a trial export.
          </p>
        )}

        <div className="grid sm:grid-cols-2 gap-2">
          <Button onClick={handleDownload} variant="outline" className="gap-2">
            <Download className="h-4 w-4" /> {side === "front" ? "Front" : "Back"} as PNG
          </Button>
          <Button onClick={handlePdf} className="gap-2">
            <Download className="h-4 w-4" /> Print-ready PDF (front + back, bleed)
          </Button>
        </div>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
